import PlaceModel               from '@models/map/PlaceModel';
import PlaceTypeModel           from '@models/map/PlaceTypeModel';
import currentEditorStore       from '@/TokamapEditor/stores/currentEditorStore';
import { fetchEntityFromPlace } from '@/TokamapEditor/stores/EditorMercureListener';
import themeStore               from '@/TokamapEditor/stores/themeStore';
import { Shape }                from '@tokamap/core';
import { PLACE_LABEL_SOURCE }   from '@/TokamapEditor/helpers/placeLinkedContent';
import { PLACE_IMAGE_SOURCE }   from '@/TokamapEditor/helpers/placeLinkedContent';
import canStackPlaceContent     from '@/TokamapEditor/helpers/stackedPlaceLayout';
import asSingleHistoryEntry     from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import { STACKED_TEXT }         from '@/TokamapEditor/helpers/stackedPlaceLayout';
import { STACKED_IMAGE }        from '@/TokamapEditor/helpers/stackedPlaceLayout';

const LINKED_IMAGE_RADIUS = 0.5;
// Texte seul (sans image à empiler) : 100 %, la taille par défaut, remplissait toute la forme.
const LINKED_TEXT_SIZE = 0.6;

export default async function linkPlaceToShape(shapeIds: string | string[], place: PlaceModel) {
	const entity = await fetchEntityFromPlace(place, false);

	await asSingleHistoryEntry(() => applyPlaceLink(shapeIds, place, entity));
}

async function applyPlaceLink(shapeIds: string | string[], place: PlaceModel, entity: any) {
	const editor = currentEditorStore.get;
	const imageSrc = entity?.entity.image || '';

	const ids = Array.isArray(shapeIds) ? shapeIds : [shapeIds];
	const shapes = ids
		.map(id => editor.getAllShapes().find(s => s.id === id))
		.filter((s): s is NonNullable<typeof s> => s !== undefined);

	// Une seule entrée d'historique/sauvegarde pour tout le groupe (voir CLAUDE.md "Sélection
	// multiple") : seules les formes qui n'ont pas déjà `action: 'open_place'` sont incluses, pour ne
	// pas ajouter d'entrée sans effet pour celles qui l'ont déjà.
	const idsNeedingAction = shapes.filter(s => s.action !== 'open_place').map(s => s.id);

	if (idsNeedingAction.length) {
		editor.setShapeAction(idsNeedingAction, 'open_place');
	}

	editor.setShapeEntity(ids, entity);

	const contentShapes = shapes.filter(s => s.shapeKind !== 'line');
	const stackedIds = imageSrc ? contentShapes.filter(canStackPlaceContent).map(s => s.id) : [];
	const plainIds = contentShapes.map(s => s.id).filter(id => !stackedIds.includes(id));

	const text = { text: place.label || '', source: PLACE_LABEL_SOURCE };
	const image = { src: imageSrc, source: PLACE_IMAGE_SOURCE, radius: LINKED_IMAGE_RADIUS };

	if (plainIds.length) {
		// Taille posée seulement là où la forme n'en avait pas : un réglage fait à la main reste.
		const unsizedIds = plainIds.filter(id => {
			const shapeText = shapes.find(s => s.id === id)?.text;

			return shapeText?.size === undefined && shapeText?.fixedSizePx === undefined;
		});
		const sizedIds = plainIds.filter(id => !unsizedIds.includes(id));

		if (unsizedIds.length) editor.setShapeText(unsizedIds, { ...text, size: LINKED_TEXT_SIZE });
		if (sizedIds.length) editor.setShapeText(sizedIds, text);

		await editor.setShapeImage(plainIds, image);
	}

	if (stackedIds.length) {
		editor.setShapeText(stackedIds, { ...text, ...STACKED_TEXT });
		await editor.setShapeImage(stackedIds, { ...image, ...STACKED_IMAGE });
	}

	const placeType = await fetchPlaceType(place);

	if (!placeType) return;

	// La bordure reprend la couleur du type : la forme se rattache visuellement à sa catégorie dès la
	// liaison. Les formes-images n'ont pas de bordure affichée.
	const borderIds = shapes.filter(s => s.shapeKind !== 'image').map(s => s.id);

	if (placeType.color && borderIds.length) {
		editor.setShapeStyle(borderIds, { borderColor: placeType.color });
	}

	await applyPlaceTypeTheme(shapes, placeType);
}

async function fetchPlaceType(place: PlaceModel) {
	const placeTypeId = place.getId('placeType');

	if (!placeTypeId) return undefined;

	const placeType = new PlaceTypeModel({ id: placeTypeId });

	await placeType.fetch();

	return placeType;
}

// Le thème du type s'applique de lui-même à la liaison, sans question. Seulement aux formes qui n'ont
// pas encore de thème : un thème choisi à la main sur la forme n'est pas écrasé en silence.
async function applyPlaceTypeTheme(shapes: Shape[], placeType: PlaceTypeModel) {
	const themeId = placeType.getResolvableId('theme');

	if (themeId === undefined) return;

	if (!themeStore.isLoaded) await themeStore.load();

	const theme = themeStore.getTheme(String(themeId));

	if (!theme) return;

	const eligibleIds = shapes
		.filter(s => s.shapeKind !== 'image' && !s.themeId)
		.map(s => s.id);

	if (eligibleIds.length) currentEditorStore.get.setShapeThemeId(eligibleIds, theme.id);
}
